import React, {
    ReactElement, useContext,
    useEffect,
    useMemo, useState,
} from "react";

export enum HelpTypes {
    HELP = "help",
    FEEDBACK = "feedback",
}

interface HelpContextAction {
    setArticleId(articleId: string | undefined): void;
    getArticleId(): string | undefined;
    show(type: HelpTypes): void;
    on(type: HelpTypes, handler: () => void): void;
    off(type: HelpTypes, handler: () => void): void;
}

export const HelpContext = React.createContext<HelpContextAction>({
    setArticleId() {
        // do nothing
    },
    getArticleId() : string | undefined {
        return undefined;
    },
    show() {
        // do nothing
    },
    on() {
        // do nothing
    },
    off() {
        // do nothing
    },
});

type HelperContainer = Record<HelpTypes, (() => void)[]>;
export function HelpProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [articleId, setArticleId] = useState<string>();
    const [showHelpHandlers, setShowHelpHandlers] = useState<HelperContainer>(Object.create(null));

    const instance = useMemo<HelpContextAction>(() => ({
        setArticleId(id: string) {
            setArticleId(id);
        },
        getArticleId() {
            return articleId;
        },
        show(type: HelpTypes) {
            showHelpHandlers[type]?.forEach(handler => handler());
        },
        on(type, handler: () => void) {
            setShowHelpHandlers(current => {
                const copy = { ...current };
                copy[type] ??= [];
                copy[type].push(handler);
                return copy;
            });
        },
        off(type, handler: () => void) {
            setShowHelpHandlers(current => {
                if (!current[type]) {
                    return current;
                }
                const copy = { ...current };
                copy[type] = copy[type].filter(item => item !== handler);
                return copy;
            });
        },

    }), [articleId, showHelpHandlers]);

    return (
        <HelpContext.Provider value={instance}>
            {children}
        </HelpContext.Provider>
    );
}

export default function useArticleContext(articleId: string | undefined): void {
    const context = useContext(HelpContext) ?? {};

    useEffect(() => {
        context.setArticleId(articleId);
    }, [articleId]);

    useEffect(() => () => {
        context.setArticleId(undefined);
    }, []);
}

export function useOnHelpEvent(type: HelpTypes, handler: (() => void)): void {
    const context = useContext(HelpContext) ?? {};

    useEffect(() => {
        context.on(type, handler);
        return () => {
            context.off(type, handler);
        };
    }, []);
}

export function useHelpContext(): HelpContextAction {
    return useContext(HelpContext) ?? {};
}
